'use client';

import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { EnvironmentConfig, SessionRole } from '../types';

export interface ApiServiceOptions {
  environmentConfig: EnvironmentConfig;
  token?: string | null;
  onTokenUpdate?: (token: string) => void;
  onTokenClear?: () => void;
}

export interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  skipAuth?: boolean;
  captureErrors?: boolean;
}

export interface AnonymousTokenResponse {
  token: string;
  sessionId: string;
  storeId: string;
  accountId: string;
  customerId: string;
  role: 'anonymous';
}

export interface SessionInitResponse {
  locale: string;
  messages: Record<string, string>;
  store: {
    id: string;
    name: string;
    domain: string;
    currency: string;
    locale: string;
    integrations?: any[];
  };
  customer?: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    isAuthenticated: boolean;
    role: SessionRole;
  };
}

export class ApiService {
  private config: EnvironmentConfig;
  private token: string | null;
  private onTokenUpdate?: (token: string) => void;
  private onTokenClear?: () => void;

  constructor(options: ApiServiceOptions) {
    this.config = options.environmentConfig;
    this.token = options.token || null;
    this.onTokenUpdate = options.onTokenUpdate;
    this.onTokenClear = options.onTokenClear;
  }

  updateToken(token: string | null) {
    this.token = token;
  }

  getCurrentToken(): string | null {
    return this.token;
  }

  updateConfig(config: EnvironmentConfig) {
    this.config = config;
  }

  /**
   * Store the store ID in localStorage (deprecated - use usePluginConfig instead)
   * @deprecated Use usePluginConfig hook to get storeId
   */
  storeStoreId(storeId: string) {
    console.warn('[SDK] storeStoreId is deprecated. Use usePluginConfig hook to get storeId.');
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('tagada_store_id', storeId);
      }
    } catch (error) {
      console.warn('[SDK] Failed to store store ID in localStorage:', error);
    }
  }

  /**
   * Get the store ID from localStorage (deprecated - use usePluginConfig instead)
   * @deprecated Use usePluginConfig hook to get storeId
   */
  getStoredStoreId(): string | null {
    console.warn('[SDK] getStoredStoreId is deprecated. Use usePluginConfig hook to get storeId.');
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem('tagada_store_id');
      }
    } catch (error) {
      console.warn('[SDK] Failed to get store ID from localStorage:', error);
    }
    return null;
  }

  /**
   * Make an authenticated request to the API
   */
  async fetch<T = any>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, params, skipAuth = false, captureErrors = true } = options;

    // Build the full URL
    const url = `${this.config.apiConfig.baseUrl}${endpoint}`;

    // If auth is required and token is not available, throw an error
    if (!skipAuth && !this.token) {
      throw new Error('No authentication token available');
    }

    try {
      // Prepare headers
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      };

      // Add CMS token to headers if available and not skipped
      if (!skipAuth && this.token) {
        requestHeaders['x-cms-token'] = this.token;
      }

      // Create axios config
      const config: AxiosRequestConfig = {
        method,
        url,
        headers: requestHeaders,
        params,
        data: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
      };

      console.log(`[SDK] Making ${method} request to: ${url}`);
      if (body) {
        console.log(`[SDK] Request body:`, body);
      }

      // Make the request
      const response = await axios(config);

      // Check for X-TGD-STOREID header in response and store it
      const storeIdHeader = response.headers['x-tgd-storeid'] || response.headers['X-TGD-STOREID'];
      if (storeIdHeader && typeof storeIdHeader === 'string') {
        console.log(`[SDK] Store ID received from header: ${storeIdHeader}`);
        this.storeStoreId(storeIdHeader);
      }

      console.log(`[SDK] Response status: ${response.status}`);
      console.log(`[SDK] Response data:`, response.data);

      return response.data;
    } catch (error) {
      if (captureErrors) {
        console.error(`[SDK] Request failed: ${method} ${url}`, error);
      }

      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.status === 401) {
          // Token might be expired, clear it
          if (this.onTokenClear) {
            this.onTokenClear();
          }
        }

        throw new Error(
          `API request failed: ${axiosError.response?.status} ${axiosError.response?.statusText}`,
        );
      }

      throw error;
    }
  }

  /**
   * Create an anonymous token for the given store
   */
  async createAnonymousToken(storeId: string): Promise<AnonymousTokenResponse> {
    const response = await this.fetch<AnonymousTokenResponse>('/api/v1/cms/session/anonymous', {
      method: 'POST',
      body: {
        storeId,
        role: 'anonymous',
      },
      skipAuth: true, // Skip auth for anonymous token creation
    });

    return response;
  }

  /**
   * Initialize a CMS session with device info and user data
   */
  async initializeSession(sessionData: {
    storeId: string;
    accountId: string;
    customerId: string;
    role: SessionRole;
    browserLocale?: string;
    queryLocale?: string;
    queryCurrency?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    browser?: string;
    browserVersion?: string;
    os?: string;
    osVersion?: string;
    deviceType?: string;
    deviceModel?: string;
    screenWidth?: number;
    screenHeight?: number;
    timeZone?: string;
  }): Promise<SessionInitResponse> {
    console.debug('[API] Initializing session');

    return this.fetch<SessionInitResponse>('/api/v1/cms/session/init', {
      method: 'POST',
      body: sessionData,
      skipAuth: false, // Use the token for session initialization
    });
  }

  /**
   * Get customer profile
   */
  async getCustomerProfile(customerId: string): Promise<any> {
    return this.fetch(`/api/v1/customer/profile/${customerId}`);
  }

  /**
   * Get session status
   */
  async getSessionStatus(sessionId: string): Promise<any> {
    return this.fetch(`/api/v1/customer/session/${sessionId}`, {
      method: 'GET',
    });
  }

  /**
   * Update customer information (email, marketing preferences)
   */
  async updateCustomer(
    customerId: string,
    data: {
      email: string;
      acceptsMarketing?: boolean;
    },
  ): Promise<any> {
    return this.fetch(`/api/v1/customers/${customerId}`, {
      method: 'POST',
      body: { data },
    });
  }

  /**
   * Update customer and checkout session information in one request
   */
  async updateCustomerAndSessionInfo(
    checkoutSessionId: string,
    data: {
      customerData: {
        email: string;
        acceptsMarketing?: boolean;
      };
      shippingAddress: {
        firstName: string;
        lastName: string;
        address1: string;
        city: string;
        country: string;
        state: string;
        postal: string;
        phone: string;
      };
      billingAddress: {
        firstName: string;
        lastName: string;
        address1: string;
        city: string;
        country: string;
        state: string;
        postal: string;
        phone: string;
      };
      differentBillingAddress: boolean;
    },
  ): Promise<{
    success: boolean;
    errors?: Record<string, { message: string; type: string }>;
    shippingCountryChanged: boolean;
    billingCountryChanged: boolean;
  }> {
    return this.fetch(`/api/v1/checkout-sessions/${checkoutSessionId}/customer-and-session-info`, {
      method: 'POST',
      body: data,
    });
  }

  /**
   * Update shipping address only (no email required)
   * Useful for fetching shipping rates before email is entered
   */
  async updateShippingAddressOnly(
    checkoutSessionId: string,
    shippingAddress: {
      firstName?: string;
      lastName?: string;
      address1?: string;
      city?: string;
      country: string;
      state?: string;
      postal?: string;
      phone?: string;
    },
  ): Promise<{
    success: boolean;
    countryChanged: boolean;
    errors?: Record<string, { message: string; type: string }>;
  }> {
    return this.fetch(`/api/v1/checkout-sessions/${checkoutSessionId}/shipping-address`, {
      method: 'POST',
      body: { shippingAddress },
    });
  }

  /**
   * Clear the authentication token
   */
  clearToken(): void {
    this.updateToken(null);
  }
}
